import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Table, Button, Form, Input, Select, Space, message, Tag, Row, Col,
    Card, Divider, Typography, DatePicker, Modal, InputNumber, Steps,
    Alert, Tooltip, Popconfirm, Switch, Spin
} from 'antd';
import {
    PlusOutlined, SyncOutlined, ClearOutlined,
    ArrowRightOutlined, SaveOutlined, SearchOutlined,
    EyeOutlined, FileExcelOutlined, FilePdfOutlined,
    DeleteOutlined, EditOutlined, CopyOutlined, HistoryOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Layout from './Layout';
import { formatPercent } from '../utils/formatPercent';

dayjs.extend(utc);
dayjs.extend(timezone);

const MOSCOW_TIMEZONE = 'Europe/Moscow';

const formatMoscowDateTime = (value) => {
    if (value === undefined || value === null || value === '' || value === false || value === 'false') {
        return '-';
    }

    const raw = String(value).trim();
    let date;

    if (/^\d+$/.test(raw)) {
        const numeric = Number(raw);
        date = raw.length === 10
            ? dayjs.unix(numeric)
            : dayjs(numeric);
    } else {
        date = dayjs(value);
    }

    return date.isValid()
        ? date.tz(MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm')
        : '-';
};

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const TASK_STATUS_OPTIONS = [
    { value: 'new', label: 'Новая' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'filled', label: 'Заполнена' },
    { value: 'rework', label: 'На доработке' },
    { value: 'approval', label: 'Согласование' },
    { value: 'analysis', label: 'Требует анализа' },
    { value: 'approved', label: 'Утверждено' }
];

const REGISTRY_STATUS_OPTIONS = [
    { value: 'draft', label: 'Черновик' },
    { value: 'approval', label: 'На согласовании' },
    { value: 'approved', label: 'Утвержден' },
    { value: 'rejected', label: 'Отклонен' },
    { value: 'paid', label: 'Оплачен' }
];

const INITIAL_API_FILTERS = {
    onlyStatus: [], stage: [], assigned_user_fio: [], status_invoices: [],
    region: [], object_id: [], filling_user: [], news_type_name: [],
    geCreated: null, leCreated: null, geExit: null, leExit: null, geUpdated: null, leUpdated: null,
    minProfitability: null, maxProfitability: null
};

const DailyReportsPage = () => {
    const [modalForm] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);

    const [registries, setRegistries] = useState([]);
    const [counterparties, setCounterparties] = useState([]);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [tasksSource, setTasksSource] = useState([]);
    const [invoicesSource, setInvoicesSource] = useState([]);

    const [activeTab] = useState('daily_report');
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [commentHistory, setCommentHistory] = useState([]);
    const [registryHistory, setRegistryHistory] = useState([]);
    const [newCommentText, setNewCommentText] = useState('');
    const [hideFullyReported, setHideFullyReported] = useState(true);
    const commentsRef = useRef(null);
    const historyRef = useRef(null);

    // Фильтры главной страницы
    const [mainSearch, setMainSearch] = useState({ text: '', ka: '', dateRange: null, status: null });

    // Состояния просмотра и редактирования
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedRegistry, setSelectedRegistry] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isHeaderEdit, setIsHeaderEdit] = useState(false);
    const [isAddRowsMode, setIsAddRowsMode] = useState(false);
    const [headerForm] = Form.useForm();

    const [viewLoading, setViewLoading] =
    useState(false);

    const [
        taskMaterialsMap,
        setTaskMaterialsMap
    ] = useState({});

    const [taskMileageExtra, setTaskMileageExtra] = useState({});

    const [selectedTaskKeys, setSelectedTaskKeys] = useState([]);
    const [selectedInvoiceKeys, setSelectedInvoiceKeys] = useState([]);
    const [enrichedTasks, setEnrichedTasks] = useState({});
    const [tasksPagination, setTasksPagination] = useState({ current: 1, pageSize: 10 });
    const [invoicesPagination, setInvoicesPagination] = useState({ current: 1, pageSize: 20 });

    const [options, setOptions] = useState({
        users: [], regions: [], objects: [], types: [], fillers: [], stages: []
    });

    const [searchText, setSearchText] = useState('');
    const [apiFilters, setApiFilters] = useState(INITIAL_API_FILTERS);

    const docType = Form.useWatch('type', modalForm);
    const selectedCpId = Form.useWatch('counterparty_id', modalForm);
    const selectedParentId = Form.useWatch('parent_id', modalForm);
    const selectedCpName = useMemo(() => {
        return counterparties.find(c => c.id === selectedCpId)?.name;
    }, [selectedCpId, counterparties]);

    // Автоматическое открытие модалки при наличии id в URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const idFromUrl = params.get('id');

        if (idFromUrl && registries.length > 0) {
            // Ищем запись в уже загруженном списке
            const record = registries.find(r => String(r.id) === idFromUrl);

            if (record) {
                viewRegistryDetails(record);

                // Опционально: очищаем URL после открытия, чтобы модалка не всплывала повторно при обновлении
                // const newRelativePathQuery = window.location.pathname;
                // window.history.replaceState(null, '', newRelativePathQuery);
            } else {
                // Если в общем списке нет (например, другая страница или фильтр),
                // можно попробовать загрузить запись напрямую из API
                const fetchSingleRegistry = async () => {
                    try {
                        const token = localStorage.getItem('token');
                        const res = await axios.get(`/api/registries/${idFromUrl}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (res.data) {
                            viewRegistryDetails(res.data);
                        }
                    } catch (e) {
                        console.error("Не удалось загрузить отчет по ID из URL", e);
                    }
                };
                fetchSingleRegistry();
            }
        }
    }, [registries.length]);

    useEffect(() => { fetchRegistries(); fetchDictionaries(); }, []);
    useEffect(() => {
        const fetchFullTasks = async () => {
            if (isViewModalVisible && selectedRegistry?.items) {
                const token = localStorage.getItem('token');
                const h = { 'Authorization': `Bearer ${token}` };

                // Собираем уникальные ВНЕШНИЕ external_id задач
                const extIds = [...new Set(selectedRegistry.items.map(i => i.invoice?.task?.external_id))]
                    .filter(extId => extId && !enrichedTasks[extId]);

                if (extIds.length === 0) return;

                try {
                    // Запрос по ВНЕШНЕМУ ID: /api/tasks/ext/{external_id}
                    const requests = extIds.map(extId => axios.get(`/api/tasks/ext/${extId}`, { headers: h }));
                    const responses = await Promise.all(requests);

                    const newBatch = {};
                    responses.forEach(res => {
                        const fullTask = res.data;
                        if (fullTask) {
                            console.log(`[Внешние данные] Задача #${fullTask.external_id || fullTask.id}:`, fullTask);
                            let cf = fullTask.custom_fields;
                            if (typeof cf === 'string') {
                                try { cf = JSON.parse(cf); } catch (e) { cf = { error: "Ошибка парсинга JSON", raw: cf }; }
                            }
                            console.log(`[Custom Fields] Задача #${fullTask.external_id || fullTask.id}:`, cf);
                            if (fullTask.id) newBatch[String(fullTask.id)] = fullTask;
                            if (fullTask.external_id) newBatch[String(fullTask.external_id)] = fullTask;
                        }
                    });
                    setEnrichedTasks(prev => ({ ...prev, ...newBatch }));

                } catch (e) {
                    console.error("Ошибка при загрузке полных данных задач по внешнему ID", e);
                }
            }
        };

        fetchFullTasks();
    }, [isViewModalVisible, selectedRegistry]);
    const fetchDictionaries = async () => {
        const token = localStorage.getItem('token');
        const h = { 'Authorization': `Bearer ${token}` };
        try {
            const [u, r, o, t, f, s, cp] = await Promise.all([
                axios.get('/api/tasks/assigned-users', { headers: h }),
                axios.get('/api/tasks/regions', { headers: h }),
                axios.get('/api/tasks/service-objects', { headers: h }),
                axios.get('/api/tasks/types', { headers: h }),
                axios.get('/api/tasks/filling-users', { headers: h }),
                axios.get('/api/tasks/statuses', { headers: h }),
                axios.get('/api/counterparties?limit=1000', { headers: h })
            ]);
            setOptions({
                users: u.data.map(v => ({ value: v, label: v })),
                regions: r.data.map(v => ({ value: v, label: v })),
                objects: o.data.map(v => ({ value: v, label: v })),
                types: t.data.map(v => ({ value: v, label: v })),
                fillers: f.data.map(v => ({ value: v.login, label: v.fio || v.login })),
                stages: s.data.map(v => ({ value: v, label: v }))
            });
            setCounterparties(cp.data.data || []);
        } catch (e) { console.error('Ошибка справочников', e); }
    };

    const fetchRegistries = async () => {
    setLoading(true);

    try {
        const token =
            localStorage.getItem(
                'token'
            );

        const response =
            await axios.get(
                '/api/registries',
                {
                    params: {
                        type:
                            'daily_report',

                        /*
                         * Пока страница использует
                         * клиентскую фильтрацию,
                         * получаем максимальное
                         * разрешённое количество.
                         */
                        page: 1,
                        limit: 200
                    },

                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

        /*
         * Новый getRegistries возвращает:
         * {
         *   data: [],
         *   total,
         *   page,
         *   limit
         * }
         *
         * Старый маршрут мог возвращать
         * непосредственно массив.
         */
        const rows =
            Array.isArray(
                response.data
            )
                ? response.data
                : Array.isArray(
                    response.data?.data
                )
                    ? response.data.data
                    : [];

        setRegistries(rows);
    } catch (error) {
        console.error(
            'Ошибка загрузки ежедневных отчётов:',
            error
        );

        setRegistries([]);

        message.error(
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Не удалось загрузить ежедневные отчёты'
        );
    } finally {
        setLoading(false);
    }
};

    const handleExport = (id) => {
        const token = localStorage.getItem('token');
        window.open(`/api/registries/${id}/daily-export?token=${token}`, '_blank');
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/registries/${selectedRegistry.id}/comments`,
                { text: newCommentText },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setNewCommentText('');
            const res = await axios.get(`/api/registries/${selectedRegistry.id}/comments`, { headers: { 'Authorization': `Bearer ${token}` } });
            setCommentHistory(res.data);
        } catch (e) { message.error("Ошибка отправки"); }
    };

    const groupedRegistryItems = useMemo(() => {
        if (!selectedRegistry?.items) return [];
        const groups = {};
        selectedRegistry.items.forEach(item => {
            const extId = item.invoice?.task?.external_id || 'Без ID';
            if (!groups[extId]) {
                groups[extId] = { task: item.invoice?.task, items: [], total: 0 };
            }
            groups[extId].items.push(item);
            groups[extId].total += parseFloat(item.invoice?.amount || 0);
        });
        return Object.values(groups);
    }, [selectedRegistry]);

    const groupedInvoicesSource = useMemo(() => {
        if (!invoicesSource || invoicesSource.length === 0) return [];

        // Оставляем только ДОХОДЫ и фильтруем по выбранному контрагенту
        const filtered = invoicesSource.filter(inv => {
            const isIncome = inv.type === 'income'; // Проверка типа

            return isIncome;
        });

        const groups = {};
        filtered.forEach(inv => {
            const extId = inv.task?.external_id || 'ID?';
            if (!groups[extId]) {
                groups[extId] = { task: inv.task, items: [], total: 0 };
            }
            groups[extId].items.push(inv);
            groups[extId].total += parseFloat(inv.amount || 0);
        });
        return Object.values(groups);
    }, [invoicesSource, selectedCpName]);

    const filteredRegistries = useMemo(() => {
        return registries.filter(r => {
            const searchVal = mainSearch.text?.toLowerCase();
            const matchText = !searchVal || r.number?.toLowerCase().includes(searchVal) || r.title?.toLowerCase().includes(searchVal) || String(r.id).includes(searchVal);
            const matchKA = !mainSearch.ka || r.counterparty_name?.toLowerCase().includes(mainSearch.ka.toLowerCase());
            const matchStatus = !mainSearch.status || r.status === mainSearch.status;
            const matchDate = !mainSearch.dateRange || (dayjs(r.date).isAfter(mainSearch.dateRange[0], 'day') && dayjs(r.date).isBefore(mainSearch.dateRange[1], 'day'));
            return matchText && matchKA && matchStatus && matchDate;
        });
    }, [registries, mainSearch]);

    const removeItemFromRegistry =
    async itemId => {
        try {
            const token =
                localStorage.getItem(
                    'token'
                );

            const headers = {
                Authorization:
                    `Bearer ${token}`
            };

            await axios.delete(
                `/api/registries/items/${itemId}`,
                {
                    headers
                }
            );

            const response =
                await axios.get(
                    `/api/registries/${selectedRegistry.id}`,
                    {
                        headers
                    }
                );

            setSelectedRegistry({
                ...response.data,
                items:
                    Array.isArray(
                        response.data?.items
                    )
                        ? response.data.items
                        : []
            });

            fetchRegistries();

            message.success(
                'Строка удалена'
            );
        } catch (error) {
            console.error(
                'Ошибка удаления строки:',
                error
            );

            message.error(
                error.response?.data?.error ||
                'Ошибка при удалении'
            );
        }
    };

    const removeTaskFromRegistry = async ({
        taskId,
        externalId
    } = {}) => {
        const registryId = selectedRegistry?.id;
        const id = Number(taskId);
        if (!registryId || !id) {
            message.error('Не удалось определить заявку');
            return;
        }

        const hasInReport = (selectedRegistry.items || []).some(
            row =>
                Number(
                    row.invoice?.task_id ||
                        row.invoice?.task?.id
                ) === id
        );

        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`
            };

            if (hasInReport) {
                await axios.delete(
                    `/api/registries/${registryId}/tasks/${id}`,
                    { headers }
                );

                const response = await axios.get(
                    `/api/registries/${registryId}`,
                    { headers }
                );

                setSelectedRegistry({
                    ...response.data,
                    items: Array.isArray(response.data?.items)
                        ? response.data.items
                        : []
                });
                fetchRegistries();
            }

            const dropInvoiceIds = new Set(
                (invoicesSource || [])
                    .filter(inv => Number(inv.task_id) === id)
                    .map(inv => inv.id)
            );
            (selectedRegistry.items || []).forEach(row => {
                if (
                    Number(
                        row.invoice?.task_id ||
                            row.invoice?.task?.id
                    ) === id
                ) {
                    const invId = Number(
                        row.invoice_id || row.invoice?.id
                    );
                    if (invId) dropInvoiceIds.add(invId);
                }
            });

            setSelectedTaskKeys(keys =>
                keys.filter(key => Number(key) !== id)
            );
            setInvoicesSource(list =>
                (list || []).filter(
                    inv => Number(inv.task_id) !== id
                )
            );
            setSelectedInvoiceKeys(keys =>
                keys.filter(key => !dropInvoiceIds.has(Number(key)))
            );

            message.success(
                `Заявка ${externalId || id} удалена из отчёта`
            );
        } catch (error) {
            console.error('Ошибка удаления заявки:', error);
            message.error(
                error.response?.data?.error ||
                    'Ошибка при удалении заявки'
            );
        }
    };

    const addItemsToRegistry = async () => {
    if (
        !selectedRegistry?.id ||
        selectedInvoiceKeys.length === 0
    ) {
        return;
    }

    setIsSaving(true);

    try {
        const token =
            localStorage.getItem('token');

        const headers = {
            Authorization:
                `Bearer ${token}`
        };

        await axios.post(
            `/api/registries/${selectedRegistry.id}/add-items`,
            {
                invoiceIds:
                    selectedInvoiceKeys
            },
            {
                headers
            }
        );

        /*
         * Повторно получаем ежедневный отчёт,
         * как после редактирования заказа.
         */
        const response = await axios.get(
            `/api/registries/${selectedRegistry.id}`,
            {
                headers
            }
        );

        setSelectedRegistry({
            ...response.data,
            items:
                Array.isArray(
                    response.data?.items
                )
                    ? response.data.items
                    : []
        });

        setSelectedInvoiceKeys([]);
        setInvoicesSource([]);
        setIsModalVisible(false);
        setIsViewModalVisible(true);
        setIsEditMode(false);
        setIsAddRowsMode(false);

        fetchRegistries();

        message.success(
            'Состав отчёта обновлён'
        );
    } catch (error) {
        console.error(
            'Ошибка добавления строк:',
            error
        );

        message.error(
            error.response?.data?.error ||
            'Ошибка при добавлении строк'
        );
    } finally {
        setIsSaving(false);
    }
};

    const editRegistryComposition = async () => {
    if (!selectedRegistry?.id) {
        return;
    }

    const taskIds = [
        ...new Set(
            (selectedRegistry.items || [])
                .map(item => item.invoice?.task_id)
                .filter(Boolean)
        )
    ];

    setIsEditMode(true);
    setIsAddRowsMode(false);
    setSelectedTaskKeys(taskIds);
    setSelectedInvoiceKeys([]);
    setModalLoading(true);

    modalForm.setFieldsValue({
        title: selectedRegistry.title,
        number: selectedRegistry.number,
        date: selectedRegistry.date
            ? dayjs(selectedRegistry.date)
            : dayjs(),
        counterparty_id:
            counterparties.find(
                item =>
                    item.name ===
                    selectedRegistry.counterparty_name
            )?.id
    });

    try {
        const token =
            localStorage.getItem('token');

        const response = await axios.get(
            '/api/registries/get-invoices-by-tasks',
            {
                params: {
                    taskIds: taskIds.join(','),
                    docType: 'daily_report',
                    registryId:
                        selectedRegistry.id
                },
                headers: {
                    Authorization:
                        `Bearer ${token}`
                }
            }
        );

        setInvoicesSource(
            Array.isArray(response.data)
                ? response.data
                : []
        );

        /*
         * Для редактирования заказа мы сразу
         * переходим к выбору позиций.
         * Здесь делаем точно так же.
         */
        setCurrentStep(1);
        setIsViewModalVisible(false);
        setIsModalVisible(true);
    } catch (error) {
        console.error(
            'Ошибка открытия редактирования состава:',
            error
        );

        message.error(
            'Не удалось загрузить строки ВДР'
        );

        setIsEditMode(false);
    } finally {
        setModalLoading(false);
    }
};

    const openAddRowsToReport = () => {
        if (!selectedRegistry?.id) {
            return;
        }

        setIsEditMode(true);
        setIsAddRowsMode(true);
        setSelectedTaskKeys([]);
        setSelectedInvoiceKeys([]);
        setInvoicesSource([]);
        setTasksSource([]);
        setSearchText('');
        setApiFilters({
            ...INITIAL_API_FILTERS,
            region: selectedRegistry.counterparty_name || null
        });
        setCurrentStep(0);
        setIsHeaderEdit(false);
        setIsViewModalVisible(false);
        setIsModalVisible(true);
    };

    const closeWizard = () => {
        setIsModalVisible(false);
        setIsAddRowsMode(false);

        if (isEditMode && selectedRegistry?.id) {
            setIsEditMode(false);
            setIsViewModalVisible(true);
        }
    };

    useEffect(() => {
        const currentType = docType || activeTab;
        if (currentType?.includes('act') || currentType === 'daily_report') {
            const token = localStorage.getItem('token');
            axios.get('/api/registries/available-orders', { params: { type: currentType }, headers: { 'Authorization': `Bearer ${token}` } }).then(res => setAvailableOrders(res.data));
        }
    }, [docType, activeTab, currentStep]);

    const handleFilterChange = (key, value) => setApiFilters(p => ({ ...p, [key]: value }));

    const handleSearchTasks = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const queryParams = {};
            if (searchText.trim()) {
                queryParams.search = searchText.trim();
            } else {
                for (const key in apiFilters) {
                    const val = apiFilters[key];
                    if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) continue;
                    if (dayjs.isDayjs(val)) queryParams[key] = val.add(3, 'hour').format('YYYY-MM-DD HH:mm:ss');
                    else queryParams[key] = Array.isArray(val) ? val.join(',') : val;
                }
            }
            const res = await axios.get('/api/tasks', { params: { ...queryParams, limit: 1000 }, headers: { 'Authorization': `Bearer ${token}` } });
            setTasksSource(res.data || []);
        } finally { setLoading(false); }
    };

    const goToInvoicesStep = async () => {
        try {
            if (selectedTaskKeys.length === 0) return message.warning('Выберите хотя бы одну задачу');
            setModalLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries/get-invoices-by-tasks', {
                params: {
                    taskIds: selectedTaskKeys.join(','),
                    docType: isEditMode ? selectedRegistry.type : (docType || activeTab || 'daily_report')
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // ПУНКТ 2: Автоматическое заполнение контрагента из первого шага
            if (apiFilters.region) {
                const foundCp = counterparties.find(c => c.name === apiFilters.region);
                if (foundCp) {
                    modalForm.setFieldsValue({ counterparty_id: foundCp.id });
                }
            }

            setInvoicesSource(res.data);
            setCurrentStep(1);
        } catch (e) { message.error('Ошибка получения данных'); } finally { setModalLoading(false); }
    };


    const handleCreate = async () => {
        try {
            const vals = await modalForm.validateFields();
            setIsSaving(true);
            const cp = counterparties.find(c => c.id === vals.counterparty_id);
            await axios.post('/api/registries', { ...vals, type: 'daily_report', counterparty_name: cp?.name, selectedInvoiceIds: selectedInvoiceKeys }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            message.success('Отчет создан');
            setIsModalVisible(false);
            fetchRegistries();
        } catch (e) { message.error('Проверьте заполнение полей'); } finally { setIsSaving(false); }
    };

    const viewRegistryDetails =
    async record => {
        const registryId =
            Number(record?.id);

        if (
            !Number.isInteger(
                registryId
            ) ||
            registryId <= 0
        ) {
            message.error(
                'Некорректный ID ежедневного отчёта'
            );

            return;
        }

        const token =
            localStorage.getItem(
                'token'
            );

        const headers = {
            Authorization:
                `Bearer ${token}`
        };

        setIsEditMode(false);
        setViewLoading(true);

        /*
         * Открываем окно сразу, но не передаём
         * в таблицы неполный объект из списка.
         */
        setSelectedRegistry({
            ...record,

            items:
                Array.isArray(
                    record?.items
                )
                    ? record.items
                    : []
        });

        setRegistryHistory([]);
        setCommentHistory([]);
        setEnrichedTasks({});
        setIsViewModalVisible(true);

        try {
            /*
             * Самое важное — получить полный документ.
             *
             * История и комментарии не должны мешать
             * открытию просмотра, если один из этих
             * дополнительных запросов завершился ошибкой.
             */
            const [
                registryResult,
                historyResult,
                commentsResult
            ] = await Promise.allSettled([
                axios.get(
                    `/api/registries/${registryId}`,
                    {
                        headers
                    }
                ),

                axios.get(
                    `/api/registries/${registryId}/history`,
                    {
                        headers
                    }
                ),

                axios.get(
                    `/api/registries/${registryId}/comments`,
                    {
                        headers
                    }
                )
            ]);

            if (
                registryResult.status !==
                'fulfilled'
            ) {
                throw (
                    registryResult.reason ||
                    new Error(
                        'Документ не загружен'
                    )
                );
            }

            const fullRegistry =
                registryResult.value?.data;

            if (
                !fullRegistry ||
                Number(fullRegistry.id) !==
                    registryId
            ) {
                throw new Error(
                    'Сервер вернул некорректный документ'
                );
            }

            /*
             * Защита от items = null.
             */
            setSelectedRegistry({
                ...fullRegistry,

                items:
                    Array.isArray(
                        fullRegistry.items
                    )
                        ? fullRegistry.items
                        : []
            });

            headerForm.setFieldsValue({
                number: fullRegistry.number || '',
                title: fullRegistry.title || '',
                date: fullRegistry.date
                    ? dayjs(fullRegistry.date)
                    : dayjs()
            });

            setIsHeaderEdit(false);

            if (
                historyResult.status ===
                'fulfilled'
            ) {
                setRegistryHistory(
                    Array.isArray(
                        historyResult.value
                            ?.data
                    )
                        ? historyResult.value.data
                        : []
                );
            } else {
                setRegistryHistory([]);

                console.warn(
                    'История ежедневного отчёта не загружена:',
                    historyResult.reason
                );
            }

            if (
                commentsResult.status ===
                'fulfilled'
            ) {
                setCommentHistory(
                    Array.isArray(
                        commentsResult.value
                            ?.data
                    )
                        ? commentsResult.value.data
                        : []
                );
            } else {
                setCommentHistory([]);

                console.warn(
                    'Комментарии ежедневного отчёта не загружены:',
                    commentsResult.reason
                );
            }
        } catch (error) {
            console.error(
                'Ошибка просмотра ежедневного отчёта:',
                error
            );

            message.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Не удалось загрузить ежедневный отчёт'
            );

            setSelectedRegistry(null);
            setIsViewModalVisible(false);
        } finally {
            setViewLoading(false);
        }
    };

    const refreshSelectedRegistry = async () => {
    if (!selectedRegistry?.id) {
        return;
    }

    const token =
        localStorage.getItem('token');

    const response = await axios.get(
        `/api/registries/${selectedRegistry.id}`,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`
            }
        }
    );

    const registry =
        response.data;

    setSelectedRegistry({
        ...registry,

        items:
            Array.isArray(registry?.items)
                ? registry.items
                : []
    });

    return registry;
};

    const createActOnBasis = async (report) => {
        setIsViewModalVisible(false);
        const actType = 'customer_act';
        modalForm.setFieldsValue({
            type: actType,
            counterparty_id: counterparties.find(c => c.name === report.counterparty_name)?.id,
            title: `На основании отчета №${report.number}`,
            date: dayjs()
        });
        const taskIds = [...new Set(report.items.map(i => i.invoice.task_id))];
        setSelectedTaskKeys(taskIds);
        setSelectedInvoiceKeys([]);
        setIsModalVisible(true);
        setModalLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/registries/get-invoices-by-tasks', { params: { taskIds: taskIds.join(','), docType: actType }, headers: { 'Authorization': `Bearer ${token}` } });
            setInvoicesSource(res.data);
            setCurrentStep(1);
        } catch (e) { message.error('Ошибка подготовки данных'); setIsModalVisible(false); } finally { setModalLoading(false); }
    };

    // ФУНКЦИЯ "ВЫБРАТЬ ВСЕ" ДЛЯ ШАГА 2
    const handleSelectAllInvoices = () => {
        const selectableIds = groupedInvoicesSource
            .flatMap(g => g.items)
            .filter(i => {
                if (i.is_used) return false;
                if (!selectedParentId) return !i.order_id;
                return !i.order_id || i.order_id === selectedParentId;
            })
            .map(i => i.id);
        setSelectedInvoiceKeys(selectableIds);
    };

    const getTaskVal = (task, key) => {
        if (!task) return '-';
        // Ищем полную версию в кеше
        const fullTask = enrichedTasks[String(task.id)] || enrichedTasks[String(task.external_id)] || task;

        try {
            // 1. Поля верхнего уровня (Системные)
            if (key === 'id') return fullTask.external_id || fullTask.id || '-';
            if (key === 'assigned_user_fio') return fullTask.assigned_user_fio || task.assigned_user_fio || '-';
            if (key === 'description') return fullTask.text || fullTask.description || task.description || '-';
            if (key === 'news_date') {
                const date = fullTask.news_date || task.news_date;
                return formatMoscowDateTime(date);
            }
            if (key === 'news_type_name') return fullTask.news_type_name || task.news_type_name || '-';

            // 2. Парсинг custom_fields
            let cf = fullTask.custom_fields;
            if (typeof cf === 'string') cf = JSON.parse(cf);
            if (!cf) return '-';

            // --- СПЕЦИАЛЬНАЯ ЛОГИКА ДЛЯ ОБОРУДОВАНИЯ (Test2_51928d...) ---
            if (key === 'Test2_51928d1c-f870-1004-8b96-85426d8be330') {
                const fieldData = cf[key];
                const valuesArray = fieldData?.value;

                if (!Array.isArray(valuesArray)) return '-';

                let materialsList = [];
                valuesArray.forEach(entry => {
                    // Достаем вложенный массив из TMTS -> value -> values
                    const items = entry.TMTS?.value?.values;
                    if (Array.isArray(items)) {
                        items.forEach(item => {
                            if (item.title) {
                                materialsList.push(`${item.title} (${item.num || 1} шт.)`);
                            }
                        });
                    }
                });

                return materialsList.length > 0 ? materialsList.join('; ') : '-';
            }

            let fieldObj = cf[key];
            let val = fieldObj?.value;

            if (val === undefined || val === null || val === '' || val === 'false') return '-';

            // 3. Обработка дат в кастомных полях
            // Время появления и покидания сайта приходит Unix timestamp в UTC.
// Для этих двух полей выводим московское время UTC+3.
if (
    key === 'Vremja_pojavlenija_na_sayte' ||
    key === 'Vremja_pokidanija_sayta'
) {
    const timestamp = Number(val);

    if (!Number.isFinite(timestamp)) {
        return '-';
    }

    return formatMoscowDateTime(timestamp);
}

            return val;
        } catch (e) {
            return '-';
        }
    };

    const normalizeArticleName = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

const isMileageInvoice = invoice => {
    const article = String(invoice?.article || '').toLowerCase();
    const rateName = String(invoice?.rate_name || '').toLowerCase();

    return article.includes('пробег') ||
        article.includes('километр') ||
        rateName.includes('километр') ||
        article.includes('вызов');
};

const isRequiredMaterialInvoice = invoice => {
    const article =
        normalizeArticleName(
            invoice?.article
        );

    // Материалы бывают и в доходе (продажа заказчику), и в расходе
    return (
        article.startsWith(
            '13. давальческое оборудование'
        ) ||
        article.startsWith(
            '12. материалы'
        )
    );
};

/*
 * Один материал может быть и в доходе, и в расходе одной заявки —
 * количество суммируем внутри типа и берём большее, чтобы не удваивать.
 */
const buildMaterialLines = invoices => {
    const groups = new Map();

    invoices.forEach(invoice => {
        const name = String(invoice?.rate_name || invoice?.article || '').trim();
        if (!name) return;

        const note = String(invoice?.note || '').trim();
        const unit = String(invoice?.unit || '').trim();
        const key = `${name}|${note}|${unit}`;

        if (!groups.has(key)) {
            groups.set(key, { name, note, unit, byType: {} });
        }

        const group = groups.get(key);
        const type = invoice?.type || 'other';
        group.byType[type] = (group.byType[type] || 0) + (Number(invoice?.quantity) || 0);
    });

    return [...groups.values()].map(({ name, note, unit, byType }) => {
        const quantity = Math.max(0, ...Object.values(byType));
        const quantityText = quantity
            ? ` — ${quantity.toLocaleString('ru-RU', { maximumFractionDigits: 3 })} ${unit || 'шт.'}`
            : '';

        return `${name}${quantityText}${note ? ` (${note})` : ''}`;
    });
};

const getTaskMaterials = (
    task,
    fallbackTaskId = null
) => {
    const possibleIds = [
        fallbackTaskId,
        task?.task_id,
        task?.id,
        task?.db_id,
        task?.internal_id
    ]
        .map(Number)
        .filter(id =>
            Number.isInteger(id) &&
            id > 0
        );

    for (const taskId of possibleIds) {
        if (
            Object.prototype.hasOwnProperty.call(
                taskMaterialsMap,
                taskId
            )
        ) {
            return (
                taskMaterialsMap[taskId] ||
                ''
            );
        }
    }

    return '';
};

    const taskSummaryData = useMemo(() => {
        if (!selectedRegistry?.items || selectedRegistry.items.length === 0) return [];

        const map = {};

        selectedRegistry.items.forEach(item => {
            const originalTask = item.invoice?.task;
            if (!originalTask) return; // Пропускаем, если нет данных о задаче

            // Ищем в кеше. Важно: приводим ключи к строке для надежности
            const task = enrichedTasks[String(originalTask.id)] ||
                enrichedTasks[String(originalTask.external_id)] ||
                originalTask;

            // Если внешнего ID нет, используем внутренний ID как ключ группировки
            const taskKey = task.external_id || task.id;
            if (!taskKey) return;

            if (!map[taskKey]) {
    const internalTaskId =
        Number(
            item.invoice?.task_id ||
            originalTask?.id
        ) || null;

    map[taskKey] = {
        task,
        internalTaskId,

        sum_total: 0,
        sum_mileage: 0,
        sum_works: 0,
        sum_materials: 0,

        materials:
            getTaskMaterials(
                originalTask,
                internalTaskId
            ),

        mileage_val: 0
    };
}

            const amount = parseFloat(item.invoice?.amount || 0);
            const qty = parseFloat(item.invoice?.quantity || 0) || 0;
            const article = (item.invoice?.article || '').toLowerCase();
            const rateName = (item.invoice?.rate_name || '').toLowerCase();

            const isMileage = isMileageInvoice(item.invoice);

            const isMaterial = article.includes('материал') ||
                article.includes('оборудование') ||
                rateName.includes('материал');

            if (isMileage) {
                map[taskKey].sum_mileage += amount;
                map[taskKey].mileage_val += qty;
            } else if (isMaterial) {
                map[taskKey].sum_materials += amount;
            } else {
                map[taskKey].sum_works += amount;
            }

            map[taskKey].sum_total += amount;
        });

        Object.values(map).forEach(row => {
            const extra = taskMileageExtra[row.internalTaskId];
            if (!extra) return;
            row.mileage_val += extra.qty;
            row.sum_mileage += extra.amount;
        });

        return Object.values(map);
    }, [
    selectedRegistry,
    enrichedTasks,
    taskMaterialsMap,
    taskMileageExtra
]);
    const handleExportExcel = async (record) => {
        const hide = message.loading('Формирование полной выгрузки...', 0);

        try {
            const workbook = new ExcelJS.Workbook();

            const headerStyle = {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '70AD47' } },
                font: { color: { argb: 'FFFFFF' }, bold: true },
                alignment: { vertical: 'middle', horizontal: 'center', wrapText: true },
                border: {
                    top: {style:'thin'}, left: {style:'thin'},
                    bottom: {style:'thin'}, right: {style:'thin'}
                }
            };

            const dataStyle = {
                alignment: { vertical: 'middle', wrapText: true },
                border: {
                    top: {style:'thin'}, left: {style:'thin'},
                    bottom: {style:'thin'}, right: {style:'thin'}
                }
            };

            // --- ВКЛАДКА 1: СВОДНЫЙ ОТЧЕТ (как Таблица 1 в UI) ---
            const ws1 = workbook.addWorksheet('Ежедневный отчёт');
            ws1.columns = [
                { header: 'ID заявки', key: 'id', width: 12 },
                { header: 'БС', key: 'bs', width: 12 },
                { header: 'Дата создания', key: 'date', width: 18 },
                { header: 'Номер ТТ', key: 'tt', width: 15 },
                { header: 'Текст задания', key: 'desc', width: 40 },
                { header: 'Время появления на сайте', key: 'in', width: 18 },
                { header: 'Время покидания сайта', key: 'out', width: 18 },
                { header: 'Исполнитель', key: 'user', width: 25 },
                { header: 'Представитель заказчика', key: 'userzk', width: 25 },
                { header: 'Вид работы*', key: 'type', width: 20 },
                { header: 'Описание выполненных работ', key: 'work', width: 40 },
                { header: 'Сумма по заявке', key: 'final', width: 18 },
                { header: 'Км (1.1.2)', key: 'mil_v', width: 12 },
                { header: 'Сумма км', key: 'mil_s', width: 15 },
                { header: 'Сумма по работам', key: 'work_s', width: 15 },
                { header: 'Оборудование и Материалы', key: 'mat_s', width: 15 },

                { header: 'Итоговая стоимость Заявки (руб. с НДС)', key: 'final22', width: 18 }
            ];

            taskSummaryData.forEach(item => {
                const row = ws1.addRow({
                    id: item.task?.external_id || item.task?.id,
                    bs: getTaskVal(item.task, 'Nomer_BS'),
                    date: formatMoscowDateTime(item.task?.news_date),
                    tt: getTaskVal(item.task, 'Nomer_TT'),
                    desc: getTaskVal(item.task, 'description'),
                    in: getTaskVal(item.task, 'Vremja_pojavlenija_na_sayte'),
                    out: getTaskVal(item.task, 'Vremja_pokidanija_sayta'),
                    user: getTaskVal(item.task, 'assigned_user_fio'),
                    userzk: getTaskVal(item.task, 'customer'),
                    type: item.task?.news_type_name || '-',
                    work: getTaskVal(item.task, 'Opisanie_vepolnenneh_rabot'),
                    mil_v: item.mileage_val,
                    mil_s: item.sum_mileage,
                    work_s: item.sum_works,
                    mat_s:
    getTaskMaterials(
        item.task,
        item.internalTaskId
    ),
                    final: item.sum_total,
                    final22: item.sum_total*1.22
                });
                row.eachCell(c => c.style = dataStyle);
                
            });
            ws1.getRow(1).eachCell(c => c.style = headerStyle);

            // --- ВКЛАДКА 2: ДЕТАЛИЗАЦИЯ (Полная копия Таблицы 2 из UI) ---
            const ws2 = workbook.addWorksheet('Счёт-фактура');
            ws2.columns = [
                { header: 'ID заявки', key: 'id', width: 12 },
                { header: 'БС', key: 'bs', width: 12 },
                { header: 'Дата создания', key: 'date', width: 18 },
                { header: 'Номер ТТ', key: 'tt', width: 15 },
                { header: 'Текст задания', key: 'title', width: 30 },
                { header: 'Время появления на сайте', key: 'in', width: 18 },
                { header: 'Время покидания сайта', key: 'out', width: 18 },
                { header: 'Исполнитель', key: 'user', width: 25 },
                { header: 'Представитель Заказчика', key: 'client', width: 25 },
                { header: 'Вид работы*', key: 'type', width: 20 },
                { header: 'Описание выполненных работ', key: 'work', width: 40 },
                { header: 'Оборудование и Материалы', key: 'note', width: 25 },
                { header: 'Позиция ТЦП', key: 'rate', width: 40 },
                { header: 'Кол-во', key: 'qty', width: 10 },
                { header: 'Цена за ед', key: 'price', width: 12 },
                { header: 'Сумма по позиции', key: 'amt', width: 15 }
            ];

            selectedRegistry.items.forEach(item => {
    const inv =
        item.invoice;

    const task =
        inv?.task;

    const internalTaskId =
        Number(
            inv?.task_id ||
            task?.id
        ) || null;

    const materials =
        getTaskMaterials(
            task,
            internalTaskId
        );

    const row = ws2.addRow({
        id:
            getTaskVal(
                task,
                'id'
            ),

        bs:
            getTaskVal(
                task,
                'Nomer_BS'
            ),

        date:
            getTaskVal(
                task,
                'news_date'
            ),

        tt:
            getTaskVal(
                task,
                'Nomer_TT'
            ),

        title:
            task?.title || '-',

        in:
            getTaskVal(
                task,
                'Vremja_pojavlenija_na_sayte'
            ),

        out:
            getTaskVal(
                task,
                'Vremja_pokidanija_sayta'
            ),

        user:
            getTaskVal(
                task,
                'assigned_user_fio'
            ),

        client:
            getTaskVal(
                task,
                'customer'
            ),

        type:
            getTaskVal(
                task,
                'news_type_name'
            ),

        work:
            getTaskVal(
                task,
                'Opisanie_vepolnenneh_rabot'
            ),

        note:
            materials,

        rate:
            inv?.rate_name || '',

        qty:
            Number(
                inv?.quantity
            ) || 0,

        price:
            Number(
                inv?.price
            ) || 0,

        amt:
            Number(
                inv?.amount
            ) || 0
    });

    row.eachCell(cell => {
        cell.style = dataStyle;
    });

    /*
     * Колонка L — оборудование и материалы.
     */
    const materialCell =
        row.getCell(12);

    materialCell.value =
        materials;

    materialCell.alignment = {
        vertical:
            'top',

        horizontal:
            'left',

        wrapText:
            true
    };

    const lineCount =
        String(materials)
            .split('\n')
            .filter(Boolean)
            .length;

    if (lineCount > 1) {
        row.height =
            Math.max(
                18,
                lineCount * 15
            );
    }
});
            ws2.getRow(1).eachCell(c => c.style = headerStyle);

            // Сохранение файла
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `DailyReport_${record.number || record.id}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);

            message.success('Файл успешно сформирован');
        } catch (e) {
            console.error('Excel Export Error:', e);
            message.error('Ошибка при генерации Excel');
        } finally {
            hide();
        }
    };


    const handleDeleteRegistry = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/registries/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            message.success('Отчет удален');
            fetchRegistries();
        } catch (e) {
            message.error('Ошибка при удалении отчета');
        }
    };

    const updateRegistryStatus = async (newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/registries/${selectedRegistry.id}/status`,
                { status: newStatus },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            message.success('Статус обновлен');

            // Обновляем локальное состояние, чтобы изменения были видны сразу
            setSelectedRegistry(prev => ({ ...prev, status: newStatus }));
            fetchRegistries(); // Обновляем основной список
        } catch (e) {
            message.error('Ошибка при смене статуса');
        }
    };

    const handleSaveMetadata = async () => {
        try {
            if (!selectedRegistry?.id) {
                return;
            }

            const values = await headerForm.validateFields();
            const token = localStorage.getItem('token');

            const payload = {
                ...values,
                date: values.date
                    ? values.date.format('YYYY-MM-DD')
                    : null
            };

            await axios.put(
                `/api/registries/${selectedRegistry.id}/metadata`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const response = await axios.get(
                `/api/registries/${selectedRegistry.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setSelectedRegistry({
                ...response.data,
                items: Array.isArray(response.data?.items)
                    ? response.data.items
                    : []
            });

            headerForm.setFieldsValue({
                number: response.data.number || '',
                title: response.data.title || '',
                date: response.data.date
                    ? dayjs(response.data.date)
                    : dayjs()
            });

            setIsHeaderEdit(false);
            await fetchRegistries();

            message.success('Реквизиты отчёта обновлены');
        } catch (error) {
            console.error(
                'Ошибка сохранения реквизитов ежедневного отчёта:',
                error
            );

            message.error(
                error.response?.data?.error ||
                'Не удалось сохранить реквизиты отчёта'
            );
        }
    };


    const filteredTasksSource = useMemo(() => {
        if (!hideFullyReported) return tasksSource;
        return tasksSource.filter(task => {
            const total = parseInt(task.total_income_count || 0);
            const assigned = parseInt(task.assigned_income_count || 0);

            // Скрываем, если (позиций > 0) И (все позиции уже в отчетах)
            if (total > 0 && total === assigned) return false;
            return true;
        });
    }, [tasksSource, hideFullyReported]);
useEffect(() => {
    const loadTaskMaterials = async () => {
        if (
            !isViewModalVisible ||
            !Array.isArray(
                selectedRegistry?.items
            ) ||
            selectedRegistry.items.length === 0
        ) {
            setTaskMileageExtra({});
            return;
        }

        const taskIds = [
            ...new Set(
                selectedRegistry.items
                    .map(item =>
                        Number(
                            item.invoice?.task_id ||
                            item.invoice?.task?.id
                        )
                    )
                    .filter(taskId =>
                        Number.isInteger(taskId) &&
                        taskId > 0
                    )
            )
        ];

        if (taskIds.length === 0) {
            setTaskMaterialsMap({});
            setTaskMileageExtra({});
            return;
        }

        const invoiceIdsInReport = new Set(
            selectedRegistry.items
                .map(item => Number(item.invoice_id || item.invoice?.id))
                .filter(id => Number.isInteger(id) && id > 0)
        );

        try {
            const token =
                localStorage.getItem(
                    'token'
                );

            const headers = {
                Authorization:
                    `Bearer ${token}`
            };

            const responses =
                await Promise.all(
                    taskIds.map(taskId =>
                        axios.get(
                            `/api/tasks/${taskId}/invoices`,
                            {
                                headers
                            }
                        )
                    )
                );

            const result = {};
            const mileageExtra = {};

            responses.forEach(
                (response, index) => {
                    const taskId =
                        taskIds[index];

                    const income =
                        Array.isArray(
                            response.data?.income
                        )
                            ? response.data.income
                            : [];

                    const expense =
                        Array.isArray(
                            response.data?.expense
                        )
                            ? response.data.expense
                            : [];

                    const allInvoices = [
                        ...income,
                        ...expense
                    ];

                    result[taskId] = buildMaterialLines(
                        allInvoices.filter(
                            isRequiredMaterialInvoice
                        )
                    ).join('\n');

                    mileageExtra[taskId] = allInvoices.reduce(
                        (acc, invoice) => {
                            const invoiceId = Number(invoice?.id);
                            if (
                                invoiceIdsInReport.has(invoiceId) ||
                                Number(invoice?.include) === 0 ||
                                !isMileageInvoice(invoice)
                            ) {
                                return acc;
                            }

                            acc.qty += parseFloat(invoice.quantity || 0) || 0;
                            acc.amount += parseFloat(invoice.amount || 0) || 0;
                            return acc;
                        },
                        { qty: 0, amount: 0 }
                    );
                }
            );

            setTaskMaterialsMap(
                result
            );
            setTaskMileageExtra(
                mileageExtra
            );
        } catch (error) {
            console.error(
                'Ошибка загрузки оборудования и материалов ВДР:',
                error
            );

            setTaskMaterialsMap({});
            setTaskMileageExtra({});
        }
    };

    loadTaskMaterials();
}, [
    isViewModalVisible,
    selectedRegistry
]);

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Title level={2}>Ежедневные отчеты</Title>
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => { setIsEditMode(false); setIsAddRowsMode(false); setIsModalVisible(true); setCurrentStep(0); setSelectedTaskKeys([]); setSelectedInvoiceKeys([]); modalForm.resetFields(); }}>Создать отчет</Button>
                </Row>

                <Card style={{ marginBottom: 20 }}>
                    <Row gutter={16}>
                        <Col span={6}><Input placeholder="Поиск по № или названию" prefix={<SearchOutlined />} value={mainSearch.text} onChange={e => setMainSearch(p => ({ ...p, text: e.target.value }))} /></Col>

                        <Col span={6}><RangePicker style={{ width: '100%' }} value={mainSearch.dateRange} onChange={v => setMainSearch(p => ({ ...p, dateRange: v }))} /></Col>
                        <Col span={6}><Select placeholder="Статус" style={{ width: '100%' }} allowClear value={mainSearch.status} onChange={v => setMainSearch(p => ({ ...p, status: v }))}><Select.Option value="draft">Черновик</Select.Option><Select.Option value="approved">Утвержден</Select.Option><Select.Option value="paid">Оплачен</Select.Option></Select></Col>
                    </Row>
                </Card>

                <Card>
                    <Table
                        dataSource={filteredRegistries}
                        rowKey="id"
                        columns={[
                            { title: 'Вн. №', dataIndex: 'id', width: 80 },
                            { title: 'Дата док.', dataIndex: 'date', render: d => dayjs(d).format('DD.MM.YYYY') },
                            { title: 'Номер док.', dataIndex: 'number' },
                            { title: 'Наименование', dataIndex: 'title' },
                            { title: 'Контрагент', dataIndex: 'counterparty_name' },
                            {
                                title: 'Создан',
                                dataIndex: 'createdAt', // Sequelize отдает данные именно в этом ключе
                                render: d => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-'
                            },
                            { title: 'Создатель', dataIndex: ['user', 'fio'], render: v => v || 'Система' },
                            { title: 'Сумма', dataIndex: 'total_amount', align: 'right', render: v => `${parseFloat(v || 0).toLocaleString()} р.` },
                            {
                                title: 'Статус',
                                dataIndex: 'status',
                                render: s => {
                                    // Ищем человекочитаемое название в константах
                                    const statusObj = REGISTRY_STATUS_OPTIONS.find(opt => opt.value === s);
                                    const label = statusObj ? statusObj.label : s;

                                    // Настраиваем цвета для красоты
                                    let color = 'blue';
                                    if (s === 'draft') color = 'orange';
                                    if (s === 'approved') color = 'green';
                                    if (s === 'rejected') color = 'red';
                                    if (s === 'paid') color = 'purple';

                                    return <Tag color={color}>{label}</Tag>;
                                }
                            },
                            { title: 'Действия', key: 'actions', render: (_, record) => (
                                    <Space>
                                        <Button icon={<EyeOutlined />} onClick={() => viewRegistryDetails(record)} />

                                        <Popconfirm
                                            title="Удалить отчет?"
                                            onConfirm={() => handleDeleteRegistry(record.id)}
                                            okText="Да"
                                            cancelText="Нет"
                                        >
                                            <Button icon={<DeleteOutlined />} danger />
                                        </Popconfirm>
                                    </Space>
                                )}
                        ]}
                    />
                </Card>

                {/* МОДАЛКА ПРОСМОТРА */}
                <Modal
                    title={
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingRight: '30px' // Отступ, чтобы не перекрывать крестик закрытия
                        }}>
            <span>
                {isEditMode
                    ? `Редактирование отчета №${selectedRegistry?.number}`
                    : `Просмотр отчета №${selectedRegistry?.number}`}
            </span>
                            <Button
                                type="primary"
                                size="small"
                                icon={<FileExcelOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation(); // Чтобы клик не срабатывал на заголовок
                                    handleExportExcel(selectedRegistry);
                                }}
                            >
                                Скачать Excel
                            </Button>
                        </div>
                    }
                    open={isViewModalVisible}
                    onCancel={() => {
                        setIsHeaderEdit(false);
                        setIsViewModalVisible(false);
                    }}
                    width="90%"
                    style={{ top: 20, paddingBottom: 0 }}
                    styles={{
                        content: {
                            maxHeight: 'calc(100vh - 40px)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        },
                        header: {
                            flex: '0 0 auto',
                            marginBottom: 0,
                            paddingBottom: 16,
                            borderBottom: '1px solid #f0f0f0'
                        },
                        body: {
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                            paddingTop: 16,
                            paddingBottom: 20
                        },
                        footer: {
                            flex: '0 0 auto',
                            marginTop: 0,
                            padding: '12px 24px',
                            background: '#fff',
                            borderTop: '1px solid #f0f0f0',
                            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)'
                        }
                    }}
                    footer={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                                width: '100%',
                                flexWrap: 'wrap'
                            }}
                        >
                            <Space
                                size={20}
                                wrap
                                split={<Divider type="vertical" style={{ height: 30 }} />}
                            >
                                <div style={{ textAlign: 'left' }}>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                        Заявок
                                    </Text>
                                    <Text strong>{taskSummaryData.length}</Text>
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                        Строк ВДР
                                    </Text>
                                    <Text strong>{selectedRegistry?.items?.length || 0}</Text>
                                </div>
                                <div style={{ textAlign: 'left' }}>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                        Стоимость
                                    </Text>
                                    <Text strong style={{ fontSize: 16 }}>
                                        {(selectedRegistry?.items || [])
                                            .reduce((sum, item) => sum + (Number(item.invoice?.amount) || 0), 0)
                                            .toLocaleString('ru-RU', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}{' '}
                                        ₽
                                    </Text>
                                </div>
                            </Space>

                            <Space wrap style={{ justifyContent: 'flex-end' }}>
                                {[
    !isHeaderEdit && (
        <Button
            key="edit-header"
            icon={<EditOutlined />}
            onClick={() => {
                headerForm.setFieldsValue({
                    number: selectedRegistry?.number || '',
                    title: selectedRegistry?.title || '',
                    date: selectedRegistry?.date
                        ? dayjs(selectedRegistry.date)
                        : dayjs()
                });

                setIsHeaderEdit(true);
            }}
        >
            Редактировать реквизиты
        </Button>
    ),

    isHeaderEdit && (
        <Button
            key="cancel-header-edit"
            onClick={() => setIsHeaderEdit(false)}
        >
            Отмена
        </Button>
    ),

    isHeaderEdit && (
        <Button
            key="save-header"
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveMetadata}
        >
            Сохранить реквизиты
        </Button>
    ),

    !isHeaderEdit && (
        <Button
            key="add-rows"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={openAddRowsToReport}
        >
            Добавить строки
        </Button>
    ),

    !isHeaderEdit && (
        <Button
            key="edit-composition"
            icon={<EditOutlined />}
            onClick={editRegistryComposition}
        >
            Редактировать состав
        </Button>
    ),

    !isHeaderEdit && (
        <Button
            key="excel"
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={() =>
                handleExportExcel(
                    selectedRegistry
                )
            }
        >
            Скачать Excel
        </Button>
    ),

    !isHeaderEdit && (
        <Button
            key="close"
            onClick={() => {
                setIsHeaderEdit(false);
                setIsViewModalVisible(false);
            }}
        >
            Закрыть
        </Button>
    )
].filter(Boolean)}
                            </Space>
                        </div>
                    }
                >
                    {selectedRegistry && (
                        <div className="daily-report-view">
                            {isHeaderEdit && (
                                <Card
                                    size="small"
                                    title="Правка реквизитов ежедневного отчёта"
                                    style={{
                                        marginBottom: 20,
                                        background: '#fafafa'
                                    }}
                                >
                                    <Form
                                        form={headerForm}
                                        layout="vertical"
                                    >
                                        <Row gutter={16}>
                                            <Col xs={24} md={6}>
                                                <Form.Item
                                                    name="number"
                                                    label="Номер документа"
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={10}>
                                                <Form.Item
                                                    name="title"
                                                    label="Наименование"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Укажите наименование'
                                                        }
                                                    ]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>

                                            <Col xs={24} md={8}>
                                                <Form.Item
                                                    name="date"
                                                    label="Дата документа"
                                                >
                                                    <DatePicker
                                                        format="DD.MM.YYYY"
                                                        style={{ width: '100%' }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card>
                            )}

                            {!isHeaderEdit && (
                                <Row gutter={24} style={{ marginBottom: 20 }}>
                                    <Col span={24}>
                                        <Paragraph>
                                            <strong>Наименование: </strong>
                                            {selectedRegistry.title || '—'}
                                        </Paragraph>
                                    </Col>
                                </Row>
                            )}

                            <Row gutter={24} style={{ marginBottom: 20 }}>
                                <Col span={6}><Paragraph><strong>Контрагент:</strong> {selectedRegistry.counterparty_name}</Paragraph></Col>
                                <Col span={6}><Paragraph><strong>Дата документа:</strong> {dayjs(selectedRegistry.date).format('DD.MM.YYYY')}</Paragraph></Col>
                                <Col span={6}>
                                    <Paragraph>
                                        <strong>Статус: </strong>
                                        <Select
                                            value={selectedRegistry.status}
                                            onChange={updateRegistryStatus}
                                            style={{ width: 150 }}
                                            options={REGISTRY_STATUS_OPTIONS}
                                        />
                                    </Paragraph>
                                </Col>
                                <Col span={6}><Paragraph align="right"><strong>Итого:</strong> <Text type="danger" strong style={{fontSize: '18px'}}>{parseFloat(selectedRegistry.total_amount).toLocaleString()} р.</Text></Paragraph></Col>
                            </Row>

                            {/* ТАБЛИЦА 1: СВОДНАЯ ПО ЗАДАЧАМ (ЗЕЛЕНАЯ ШАПКА) */}
                            <Divider orientation="left">Сводный отчет по заявкам</Divider>
                            <Table
                                className="green-header-table"
                                dataSource={taskSummaryData} // Теперь здесь массив из N задач
                                rowKey={record => String(record.task?.external_id || record.task?.id)}  // Уникальный ключ для каждой строки
                                pagination={false}
                                size="small"
                                scroll={{ x: 2000 }}
                                columns={[
                                    {
                                        title: 'ID заявки',
                                        fixed: 'left',
                                        width: 90,
                                        render: (_, r) => getTaskVal(r.task, 'id') // Используем функцию вместо dataIndex
                                    },
                                    { title: 'БС', render: (_, r) => getTaskVal(r.task, 'Nomer_BS'), width: 100 },
                                    { title: 'Дата создания', render: (_, r) => dayjs(r.task.news_date).format('DD.MM.YYYY HH:mm'), width: 140 },
                                    { title: 'Номер ТТ', render: (_, r) => getTaskVal(r.task, 'Nomer_TT'), width: 110 },
                                    {
                                        title: 'Текст задания',
                                        width: 250,
                                        render: (_, r) => (
                                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '11px' }}>
                                                {getTaskVal(r.task, 'description')}
                                            </div>
                                        )
                                    },
                                    { title: 'Время появления', render: (_, r) => getTaskVal(r.task, 'Vremja_pojavlenija_na_sayte'), width: 140 },
                                    { title: 'Время покидания', render: (_, r) => getTaskVal(r.task, 'Vremja_pokidanija_sayta'), width: 140 },
                                    // ИСПРАВЛЕННЫЙ ВЫВОД ИСПОЛНИТЕЛЯ
                                    { title: 'Исполнитель', width: 150, render: (_, r) => getTaskVal(r.task, 'assigned_user_fio') },
                                    { title: 'Вид работы*', dataIndex: ['task', 'news_type_name'], width: 100 },
                                    {
                                        title: 'Описание работ',
                                        width: 250,
                                        render: (_, r) => (
                                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '11px' }}>
                                                {getTaskVal(r.task, 'Opisanie_vepolnenneh_rabot')}
                                            </div>
                                        )
                                    },
                                    { title: 'Км (1.1.2)', render: (_, r) => r.mileage_val || '-', width: 80, align: 'center' },
                                    { title: 'Сумма км', dataIndex: 'sum_mileage', align: 'right', width: 110, render: v => v.toLocaleString() },
                                    { title: 'Сумма работы', dataIndex: 'sum_works', align: 'right', width: 110, render: v => v.toLocaleString() },
                                    { title: 'Итого', dataIndex: 'sum_total', fixed: 'right', align: 'right', width: 120, render: v => <b>{v.toLocaleString()}</b> },
                                    {
                                        title: '',
                                        key: 'remove_task',
                                        fixed: 'right',
                                        width: 70,
                                        align: 'center',
                                        render: (_, r) => {
                                            const taskId =
                                                r.internalTaskId ||
                                                r.task?.id;
                                            if (!taskId) return null;
                                            const label =
                                                getTaskVal(r.task, 'id') ||
                                                taskId;
                                            return (
                                                <Popconfirm
                                                    title={`Удалить заявку ${label} из отчёта?`}
                                                    description="Будут удалены все строки этой заявки"
                                                    okText="Удалить"
                                                    cancelText="Отмена"
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={() =>
                                                        removeTaskFromRegistry({
                                                            taskId,
                                                            externalId: label
                                                        })
                                                    }
                                                >
                                                    <Button
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        title="Удалить заявку из отчёта"
                                                    />
                                                </Popconfirm>
                                            );
                                        }
                                    },
                                ]}
                                // Опционально: добавьте футер для итоговой суммы ВСЕГО отчета
                                footer={() => (
                                    <div style={{ textAlign: 'right', paddingRight: 20 }}>
                                        <Text strong>ИТОГО ПО ОТЧЕТУ: {taskSummaryData.reduce((acc, curr) => acc + curr.sum_total, 0).toLocaleString()} р.</Text>
                                    </div>
                                )}
                            />

                            {/* ТАБЛИЦА 2: ПОЗИЦИОННАЯ ДЕТАЛИЗАЦИЯ */}
                            <Divider orientation="left" style={{ marginTop: 40 }}>Детализация по позициям ТЦП</Divider>
                            <Table
                                className="green-header-table detail-table"
                                dataSource={selectedRegistry.items}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ x: 2200 }}
                                columns={[
                                    { title: 'ID заявки', dataIndex: ['invoice', 'task', 'external_id'], fixed: 'left', width: 90 },
                                    { title: 'БС', render: (_, r) => getTaskVal(r.invoice.task, 'Nomer_BS'), width: 100 },
                                    { title: 'Дата создания', render: (_, r) => dayjs(r.invoice.task.news_date).format('DD.MM.YYYY HH:mm'), width: 140 },
                                    { title: 'Номер ТТ', render: (_, r) => getTaskVal(r.invoice.task, 'Nomer_TT'), width: 100 },
                                    { title: 'Текст задания', dataIndex: ['invoice', 'task', 'title'], width: 180, ellipsis: true },
                                    { title: 'Время появления', render: (_, r) => getTaskVal(r.invoice.task, 'Vremja_pojavlenija_na_sayte'), width: 140 },
                                    { title: 'Время покидания', render: (_, r) => getTaskVal(r.invoice.task, 'Vremja_pokidanija_sayta'), width: 140 },
                                    { title: 'Исполнитель', render: (_, r) => getTaskVal(r.invoice.task, 'assigned_user_fio'), width: 150 },
                                    { title: 'Представитель Заказчика', render: (_, r) => getTaskVal(r.invoice?.task, 'customer'), width: 150 },
                                    { title: 'Вид работы*', dataIndex: ['invoice', 'task', 'news_type_name'], width: 120 },
                                    { title: 'Описание работ', dataIndex: ['invoice', 'note'], width: 150 },
                                    {
    title:
        'Оборудование и материалы',

    render: (_, record) => {
        const task =
            record.invoice?.task;

        const materials =
            getTaskMaterials(task);

        return (
            <div
                style={{
                    fontSize:
                        '10px',

                    lineHeight:
                        1.4,

                    whiteSpace:
                        'pre-wrap',

                    wordBreak:
                        'break-word'
                }}
            >
                {materials || '—'}
            </div>
        );
    },

    width:
        260
},

                                    { title: 'Позиция ТЦП', dataIndex: ['invoice', 'rate_name'], width: 250 },
                                    { title: 'Кол-во', dataIndex: ['invoice', 'quantity'], align: 'center', width: 80 },
                                    { title: 'Цена за ед', dataIndex: ['invoice', 'price'], align: 'right', width: 100, render: v => parseFloat(v).toLocaleString() },
                                    { title: 'Сумма', dataIndex: ['invoice', 'amount'], fixed: 'right', align: 'right', width: 120, render: v => <b>{parseFloat(v).toLocaleString()}</b> },
                                        {
    title: 'Действия',
    key: 'actions',
    fixed: 'right',
    width: 90,
    render: (_, record) => (
        <Popconfirm
            title="Удалить строку из отчёта?"
            okText="Да"
            cancelText="Нет"
            onConfirm={() =>
                removeItemFromRegistry(
                    record.id
                )
            }
        >
            <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
            />
        </Popconfirm>
    )
}
                                ]}
                            />
                        </div>
                    )}
                </Modal>

                {/* МАСТЕР СОЗДАНИЯ */}
                <Modal
    title={
        isAddRowsMode
            ? `Добавление строк в отчёт №${selectedRegistry?.number || selectedRegistry?.id}`
            : isEditMode
                ? `Редактирование состава отчёта №${selectedRegistry?.number || selectedRegistry?.id}`
                : 'Мастер формирования ежедневного отчета'
    } open={isModalVisible} onCancel={closeWizard} width="98%" footer={null} destroyOnClose>
                    <Form form={modalForm} layout="vertical" preserve={true}>
                        <Steps current={currentStep} items={[{ title: 'Поиск заданий' }, { title: 'Выбор позиций ВДР' }]} style={{ marginBottom: 25 }} />
                        <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                            <div style={{ background: '#f5f5f5', padding: '15px 20px', borderRadius: 8, marginBottom: 20 }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={4}><Text type="secondary">Внешний ID:</Text><Input placeholder="ID" value={searchText} onChange={e => setSearchText(e.target.value)} /></Col>
                                    <Col span={4}><Text type="secondary">Объект (БС):</Text><Select mode="multiple" style={{width:'100%'}} options={options.objects} value={apiFilters.object_id} onChange={v => handleFilterChange('object_id', v)} showSearch maxTagCount="responsive" /></Col>
                                    <Col span={4}><Text type="secondary">Заказчик (Регион):</Text><Select style={{width:'100%'}} options={options.regions} value={apiFilters.region} onChange={v => handleFilterChange('region', v)} showSearch /></Col>
                                    <Col span={4}><Text type="secondary">Вид работ:</Text><Select mode="multiple" style={{width:'100%'}} options={options.types} value={apiFilters.news_type_name} onChange={v => handleFilterChange('news_type_name', v)} showSearch /></Col>
                                    <Col span={4}><Text type="secondary">Исполнитель:</Text><Select mode="multiple" style={{width:'100%'}} options={options.users} value={apiFilters.assigned_user_fio} onChange={v => handleFilterChange('assigned_user_fio', v)} showSearch /></Col>
                                    <Col span={4}><Text type="secondary">Заполнил ВДР:</Text><Select mode="multiple" style={{width:'100%'}} options={options.fillers} value={apiFilters.filling_user} onChange={v => handleFilterChange('filling_user', v)} showSearch /></Col>
                                </Row>
                                <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                    <Col span={4}><Text type="secondary">Этапы:</Text><Select mode="multiple" style={{width:'100%'}} options={options.stages} value={apiFilters.onlyStatus} onChange={v => handleFilterChange('onlyStatus', v)} /></Col>
                                    <Col span={4}><Text type="secondary">Стадия:</Text><Select mode="multiple" style={{width:'100%'}} options={[{value:1,label:'В работе'},{value:2,label:'Завершено'}]} value={apiFilters.stage} onChange={v => handleFilterChange('stage', v)} /></Col>
                                    <Col span={4}><Text type="secondary">Статус ВДР:</Text><Select mode="multiple" style={{width:'100%'}} options={TASK_STATUS_OPTIONS} value={apiFilters.status_invoices} onChange={v => handleFilterChange('status_invoices', v)} /></Col>
                                    <Col span={6}><Text type="secondary">Рентабельность (%):</Text><div style={{display:'flex', gap: 8}}><InputNumber placeholder="От" value={apiFilters.minProfitability} onChange={v => handleFilterChange('minProfitability', v)} /><InputNumber placeholder="До" value={apiFilters.maxProfitability} onChange={v => handleFilterChange('maxProfitability', v)} /></div></Col>
                                    <Col span={6} style={{textAlign:'right', paddingTop: 22}}><Space><Button type="primary" icon={<SyncOutlined />} onClick={handleSearchTasks} loading={loading}>Найти</Button><Button danger icon={<ClearOutlined />} onClick={() => {setApiFilters(INITIAL_API_FILTERS); setSearchText('');}}>Сбросить</Button></Space></Col>
                                </Row>
                                <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
                                    <Col span={8}><Text type="secondary">Дата создания:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geCreated, apiFilters.leCreated]} onChange={v => { handleFilterChange('geCreated', v?.[0]); handleFilterChange('leCreated', v?.[1]); }} /></Col>
                                    <Col span={8}><Text type="secondary">Покидание сайта:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geExit, apiFilters.leExit]} onChange={v => { handleFilterChange('geExit', v?.[0]); handleFilterChange('leExit', v?.[1]); }} /></Col>
                                    <Col span={8}><Text type="secondary">Обновление:</Text><RangePicker showTime style={{width:'100%'}} format="DD.MM.YYYY HH:mm" value={[apiFilters.geUpdated, apiFilters.leUpdated]} onChange={v => { handleFilterChange('geUpdated', v?.[0]); handleFilterChange('leUpdated', v?.[1]); }} /></Col>
                                </Row>
                            </div>
                            {/* Перед таблицей переключатель */}
                            <div style={{ marginBottom: 10, textAlign: 'right', background: '#fff', padding: '5px 10px', borderRadius: '4px' }}>
                                <Space>
                                    <Text strong>Скрыть полностью распределенные задачи:</Text>
                                    <Switch checked={hideFullyReported} onChange={setHideFullyReported} />
                                </Space>
                            </div>
                            <Table
                                rowSelection={{
                                    selectedRowKeys: selectedTaskKeys,
                                    // ДОБАВЛЕНО: Обработчик для одиночных кликов
                                    onChange: (selectedRowKeys) => {
                                        setSelectedTaskKeys(selectedRowKeys);
                                    },
                                    onSelectAll: (selected) => {
                                        const allIds = selected ? filteredTasksSource.map(i => i.id) : [];
                                        setSelectedTaskKeys(allIds);
                                    },
                                    selections: [
                                        Table.SELECTION_ALL,
                                        Table.SELECTION_NONE,
                                    ],
                                    preserveSelectedRowKeys: true
                                }}
                                dataSource={filteredTasksSource} // <--- Важно: отфильтрованный список
                                rowKey="id"
                                size="small"
                                scroll={{ x: 1500 }}
                                columns={[
                                    { title: 'ID', dataIndex: 'id', width: 70 },
                                    { title: 'Внешний ID', dataIndex: 'external_id', width: 110 },
                                    {
                                        title: 'Название',
                                        dataIndex: 'title',
                                        render: (text, r) => {
                                            const total = parseInt(r.total_income_count || 0);
                                            const assigned = parseInt(r.assigned_income_count || 0);
                                            const hasUnassigned = total > assigned && assigned > 0;

                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Text strong={hasUnassigned}>{text}</Text>
                                                    {hasUnassigned && (
                                                        <Tag color="orange" style={{ fontSize: '10px', marginTop: 4, width: 'fit-content' }}>
                                                            Есть не вошедшие строки ({total - assigned} из {total})
                                                        </Tag>
                                                    )}
                                                    {total === 0 && <Text type="secondary" style={{fontSize:'10px'}}>Нет позиций ВДР (доход)</Text>}
                                                </div>
                                            );
                                        }
                                    },
                                    {
                                        title: 'В отчетах',
                                        dataIndex: 'registry_numbers',
                                        width: 150,
                                        render: (nums) => nums ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                {nums.split(', ').map(n => <Tag key={n} color="blue" style={{margin:0}}>{n}</Tag>)}
                                            </div>
                                        ) : <Text type="secondary">-</Text>
                                    },
                                    { title: 'Исполнитель', dataIndex: 'assigned_user_fio' },
                                    {
                                        title: 'Статус ВДР',
                                        dataIndex: 'status_invoices',
                                        render: s => {
                                            const statusObj = TASK_STATUS_OPTIONS.find(opt => opt.value === s);
                                            return <Tag color="blue">{statusObj ? statusObj.label : s}</Tag>;
                                        }
                                    }
                                ]}
                            />
                            <Button type="primary" block size="large" onClick={goToInvoicesStep} disabled={selectedTaskKeys.length === 0} style={{ marginTop: 20 }}>Выбрать позиции ({selectedTaskKeys.length} задач) <ArrowRightOutlined /></Button>
                        </div>

                        {currentStep === 1 && (
                            <>
                                <div style={{ display: isEditMode ? 'none' : 'block', marginBottom: 20, padding: 15, border: '1px solid #d9d9d9', borderRadius: 8, background: '#fff' }}>
                                    <Row gutter={16}>
                                        <Col span={5}><Form.Item name="title" label="Наименование отчета"><Input /></Form.Item></Col>
                                        <Col span={4}><Form.Item name="number" label="Номер документа"><Input placeholder="Пусто = Вн. №" /></Form.Item></Col>
                                        <Col span={5}><Form.Item name="date" label="Дата" initialValue={dayjs()} rules={[{required:true}]}><DatePicker style={{width:'100%'}} format="DD.MM.YYYY" /></Form.Item></Col>
                                        <Col span={6}><Form.Item name="counterparty_id" label="Контрагент" ><Select showSearch options={counterparties.map(c=>({label:c.name, value:c.id}))} optionFilterProp="label" /></Form.Item></Col>

                                    </Row>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                    <Alert message="Выберите строки из ВДР" type="info" showIcon style={{ flex: 1, marginRight: 15 }} />
                                    <Button type="dashed" onClick={handleSelectAllInvoices}>Выбрать все доступные строки</Button>
                                </div>
                                {groupedInvoicesSource.map((group, idx) => (
                                    <div key={idx} style={{ marginBottom: 20, border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                                        <div style={{ background: '#fafafa', padding: '8px 15px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                            <Space wrap>
                                                <Tag color="blue">#{group.task?.external_id}</Tag>
                                                <Text strong>{group.task?.title}</Text>
                                            </Space>
                                            {isEditMode && (group.task?.id || group.items?.[0]?.task_id) ? (
                                                <Popconfirm
                                                    title={`Удалить заявку ${group.task?.external_id || group.task?.id} из отчёта?`}
                                                    description="Все строки этой заявки будут убраны из документа"
                                                    okText="Удалить"
                                                    cancelText="Отмена"
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={() =>
                                                        removeTaskFromRegistry({
                                                            taskId:
                                                                group.task?.id ||
                                                                group.items?.[0]?.task_id,
                                                            externalId:
                                                                group.task?.external_id
                                                        })
                                                    }
                                                >
                                                    <Button
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                    >
                                                        Удалить заявку
                                                    </Button>
                                                </Popconfirm>
                                            ) : null}
                                        </div>
                                        <Table
                                            rowSelection={{
                                                selectedRowKeys: selectedInvoiceKeys,
                                                // ИСПРАВЛЕННАЯ ЛОГИКА ОБЪЕДИНЕНИЯ:
                                                onChange: (selectedRowKeysInGroup) => {
                                                    // 1. Получаем ID всех строк, которые принадлежат ТОЛЬКО этой текущей группе (таблице)
                                                    const currentGroupIds = group.items.map(item => item.id);

                                                    // 2. Берем все ранее выбранные ID из ДРУГИХ групп (фильтруем общий массив)
                                                    const otherGroupsSelectedKeys = selectedInvoiceKeys.filter(id => !currentGroupIds.includes(id));

                                                    // 3. Объединяем ID из других групп с новыми выбранными ID в текущей группе
                                                    setSelectedInvoiceKeys([...otherGroupsSelectedKeys, ...selectedRowKeysInGroup]);
                                                },
                                                getCheckboxProps: (r) => ({ disabled: r.is_used })
                                            }}
                                            dataSource={group.items}
                                            rowKey="id"
                                            size="small"
                                            pagination={false}
                                            rowClassName={(r) => r.is_used ? 'row-disabled' : ''}
                                            columns={[
                                                { title: 'Работа', dataIndex: 'rate_name' },
                                                { title: 'Кол-во', dataIndex: 'quantity', align: 'center', width: 80 },
                                                { title: 'Цена', dataIndex: 'price', align: 'right', width: 100, render: v => `${parseFloat(v || 0).toLocaleString()} р.` },
                                                { title: '%', dataIndex: 'percent', align: 'center', width: 70, render: v => `${formatPercent(v)}%` },
                                                { title: 'Штраф', dataIndex: 'penalty', align: 'center', width: 80, render: v => v > 0 ? <Tag color="error">-{v}%</Tag> : '-' },
                                                { title: 'Сумма', dataIndex: 'amount', align: 'right', width: 140, render: (v, r) => <Tooltip title={r.is_used ? "Уже в другом реестре" : ""}><b>{parseFloat(v).toLocaleString()} р.</b></Tooltip> }
                                            ]}
                                        />
                                    </div>
                                ))}



                                <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                                    <Button
                                        size="large"
                                        icon={<ArrowLeftOutlined />}
                                        onClick={() => setCurrentStep(0)}
                                        style={{ width: 150 }}
                                    >
                                        Назад
                                    </Button>

                                    <Button
                                        type="primary"
                                        block
                                        size="large"
                                        icon={<SaveOutlined />}
                                        onClick={isEditMode ? addItemsToRegistry : handleCreate}
                                        loading={isSaving}
                                        disabled={selectedInvoiceKeys.length === 0}
                                    >
                                        {isAddRowsMode
                                            ? `Добавить в отчёт (${selectedInvoiceKeys.length})`
                                            : isEditMode ? 'Сохранить изменения' : 'Сформировать документ'}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Form>
                </Modal>
            </div>
            <style>{
                `.row-disabled { background-color: #fafafa; opacity: 0.6; }
                .green-header-table .ant-table-thead > tr > th {
                    background-color: #70AD47 !important;
                    color: white !important;
                    font-size: 11px !important;
                    text-align: center !important;
                }
                .daily-report-view .ant-divider-inner-text {
                    font-weight: bold;
                    color: #555;
                }
                .detail-table {
                    margin-top: 10px;
                }
                .row-disabled { background-color: #fafafa; opacity: 0.6; }

            `}</style>
        </Layout>
    );
};

export default DailyReportsPage;